import { useState, useEffect, useRef, type FormEvent, type ChangeEvent } from 'react';
import { createPortal } from 'react-dom';
import { brandConfig } from '../../config/brands';
import { useCreateActor, useUpdateActor } from '../../features/admin/actors';
import type { CreateActorRequest, UpdateActorRequest } from '../../features/admin/actors';
import { isCharacterActor, getFaceScanStatusLabel } from '../../features/actors/actorAttributes';
import type { ActorPoseFields } from '../../features/actors/poses';
import { ActorPoseList } from '../shared/ActorPoseList';
import { useToast } from '../../hooks/useToast';
import { resolveErrorMessage } from '../../utils/getErrorMessage';
import styles from './ActorFormModal.module.css';
import '../../styles/buttons.css';

const SUPABASE_URL = 'https://lirsarznxaidebqhgxch.supabase.co';

// 汎用的な俳優型（共通フィールド）
export type ActorLike = {
  id: string;
  name: string;
  display_name: string;
  // キャラクター (ethnicity='character') は性別未指定 (null) がありうる
  gender: 'male' | 'female' | null;
  min_age: number | null;
  max_age: number | null;
  ethnicity: string | null;
  // キャラクター俳優専用の特徴・設定テキスト (そのままAIプロンプトへ注入される)
  character_traits?: string | null;
  tags: string[];
  character_sheet_bucket: string | null;
  character_sheet_path: string | null;
  face_image_bucket: string | null;
  face_image_path: string | null;
  // face-safe 検査状態 (キャラクター俳優のみ意味を持つ。BE は select('*') で返す)
  character_sheet_face_scan_status?: 'pending' | 'safe' | 'restricted' | 'error' | null;
} & ActorPoseFields;

// カスタムミューテーション関数の型
type CreateMutationFn = (request: CreateActorRequest) => Promise<unknown>;
type UpdateMutationFn = (params: { actorId: string; request: UpdateActorRequest }) => Promise<unknown>;
type DeleteMutationFn = (actorId: string) => Promise<unknown>;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  actor?: ActorLike | null;
  /** カスタム作成関数（指定しない場合は管理者用APIを使用） */
  createFn?: CreateMutationFn;
  /** カスタム更新関数（指定しない場合は管理者用APIを使用） */
  updateFn?: UpdateMutationFn;
  /** カスタム削除関数（指定した場合のみ削除ボタンを表示） */
  deleteFn?: DeleteMutationFn;
  /**
   * face-safe 検査 (再) 実行関数。指定した場合のみ「顔検査」行に実行ボタンを表示する
   * (省略時はボタン非表示。読み取り専用行はそのまま表示され続ける)。
   * ユーザー起点の検査は null(未検査)/error/永久pending のみ許可される
   * (BE 側で restricted は 403 に拒否される。「判定ガチャ」防止のため safe/restricted
   * の状態ではこのボタン自体を表示しない)。
   */
  onTriggerFaceScan?: (actorId: string) => Promise<unknown>;
  /**
   * ポーズの再抽出関数 (platform admin限定APIのためadmin画面だけが渡す)。
   * 指定した場合のみポーズ一覧に「再抽出」ボタンを表示する。
   */
  onRequestPoseExtraction?: (actorId: string) => Promise<unknown>;
  /** モーダルタイトルのプレフィックス */
  titlePrefix?: string;
};

// 画像拡大モーダル
function ImageLightbox({ src, alt, onClose }: { src: string; alt: string; onClose: () => void }) {
  const handleOverlayClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
  };

  const handleCloseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
  };

  return createPortal(
    <div className={styles.lightboxOverlay} onClick={handleOverlayClick}>
      <button type="button" className={styles.lightboxClose} onClick={handleCloseClick}>×</button>
      <img src={src} alt={alt} className={styles.lightboxImage} onClick={(e) => e.stopPropagation()} />
    </div>,
    document.body
  );
}

export function ActorFormModal({ isOpen, onClose, onSuccess, actor, createFn, updateFn, deleteFn, onTriggerFaceScan, onRequestPoseExtraction, titlePrefix = '' }: Props) {
  const isEditing = !!actor;
  const defaultCreateMutation = useCreateActor();
  const defaultUpdateMutation = useUpdateActor();

  // カスタム関数が提供されているかどうかでローディング状態を管理
  const [isCustomLoading, setIsCustomLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [gender, setGender] = useState<'male' | 'female' | ''>('male');
  const [minAge, setMinAge] = useState('');
  const [maxAge, setMaxAge] = useState('');
  const [ethnicity, setEthnicity] = useState('');
  const [characterTraits, setCharacterTraits] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [characterSheet, setCharacterSheet] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lightboxImage, setLightboxImage] = useState<{ src: string; alt: string } | null>(null);
  const [isFaceScanTriggering, setIsFaceScanTriggering] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  // キャラクター (ethnicity='character') は年齢・体格・身体的特徴を持たず、
  // 性別も任意入力 (未指定可) になる (P1-6)。
  const isCharacter = isCharacterActor({ ethnicity });

  // 人種変更。キャラクターへ切り替えた場合は年齢範囲をクリアする
  // (BE 側でも character 選択時は null 強制されるが、FE 表示も揃える)。
  const handleEthnicityChange = (value: string) => {
    setEthnicity(value);
    if (value === 'character') {
      setMinAge('');
      setMaxAge('');
    }
  };

  // 既存のキャラクターシート画像URL
  const existingCharacterSheetUrl = actor?.character_sheet_bucket && actor?.character_sheet_path
    ? `${SUPABASE_URL}/storage/v1/object/public/${actor.character_sheet_bucket}/${actor.character_sheet_path}`
    : null;

  // 既存の顔画像URL
  const existingFaceImageUrl = actor?.face_image_bucket && actor?.face_image_path
    ? `${SUPABASE_URL}/storage/v1/object/public/${actor.face_image_bucket}/${actor.face_image_path}`
    : null;

  // 表示するプレビュー画像を決定
  const displayPreviewUrl = previewUrl || existingCharacterSheetUrl;

  // モーダルが開いたときにフォームを初期化
  useEffect(() => {
    if (isOpen) {
      if (actor) {
        setName(actor.name);
        setDisplayName(actor.display_name);
        setGender(actor.gender || '');
        setMinAge(actor.min_age?.toString() || '');
        setMaxAge(actor.max_age?.toString() || '');
        setEthnicity(actor.ethnicity || '');
        setCharacterTraits(actor.character_traits || '');
        setTagsInput(actor.tags?.join(', ') || '');
        setCharacterSheet(null);
        setPreviewUrl(null);
      } else {
        setName('');
        setDisplayName('');
        setGender('male');
        setMinAge('');
        setMaxAge('');
        setEthnicity('');
        setCharacterTraits('');
        setTagsInput('');
        setCharacterSheet(null);
        setPreviewUrl(null);
      }
      setError(null);
    }
  }, [isOpen, actor]);

  // ファイル選択時のプレビュー生成
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCharacterSheet(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // バリデーション
    if (!isEditing && !characterSheet) {
      setError('キャラクターシート画像を選択してください');
      return;
    }
    if (!name.trim()) {
      setError('内部名を入力してください');
      return;
    }
    if (!displayName.trim()) {
      setError('表示名を入力してください');
      return;
    }
    if (!/^[a-z][a-z0-9_]*$/.test(name.trim())) {
      setError('内部名は英小文字で始まり、英小文字・数字・アンダースコアのみ使用できます');
      return;
    }
    // キャラクター (ethnicity='character') は性別未指定でもよいが、
    // 人間の俳優は従来どおり性別必須 (BE 側でも同じ不変条件を強制する)。
    if (!isCharacter && gender !== 'male' && gender !== 'female') {
      setError('性別を選択してください');
      return;
    }

    const tags = tagsInput
      .split(/[,、\n]/)
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
    const genderValue = gender === 'male' || gender === 'female' ? gender : null;

    try {
      setIsCustomLoading(true);

      if (isEditing) {
        const updateParams = {
          actorId: actor!.id,
          request: {
            display_name: displayName.trim(),
            gender: genderValue,
            min_age: minAge ? parseInt(minAge, 10) : null,
            max_age: maxAge ? parseInt(maxAge, 10) : null,
            ethnicity: ethnicity || null,
            // キャラクターのみ意味を持つ (人間俳優は BE 側で null 強制)
            character_traits: isCharacter ? (characterTraits.trim() || null) : null,
            tags,
            character_sheet: characterSheet || undefined,
          },
        };

        if (updateFn) {
          await updateFn(updateParams);
        } else {
          await defaultUpdateMutation.mutateAsync(updateParams);
        }
      } else {
        const createParams = {
          character_sheet: characterSheet!,
          name: name.trim(),
          display_name: displayName.trim(),
          gender: genderValue ?? undefined,
          min_age: minAge ? parseInt(minAge, 10) : undefined,
          max_age: maxAge ? parseInt(maxAge, 10) : undefined,
          ethnicity: ethnicity || undefined,
          character_traits: isCharacter ? (characterTraits.trim() || undefined) : undefined,
          tags,
        };

        if (createFn) {
          await createFn(createParams);
        } else {
          await defaultCreateMutation.mutateAsync(createParams);
        }
      }

      onSuccess?.();
    } catch (err: any) {
      console.error('Failed to save actor:', err);
      const errorMessage =
        err?.response?.data?.error || err?.message || '保存に失敗しました';
      setError(errorMessage);
    } finally {
      setIsCustomLoading(false);
    }
  };

  const isLoading = isCustomLoading || defaultCreateMutation.isPending || defaultUpdateMutation.isPending;

  // face-safe 検査のユーザー起点 (再) 実行。status が null/undefined/error のときのみ
  // ボタンを表示する (safe は検査済み・restricted は判定ガチャ防止のため BE 側で拒否される)。
  const handleTriggerFaceScan = async () => {
    if (!actor || !onTriggerFaceScan || isFaceScanTriggering) return;
    setIsFaceScanTriggering(true);
    try {
      await onTriggerFaceScan(actor.id);
      toast.success('顔検査を開始しました。完了まで少し時間がかかります');
    } catch (err) {
      toast.error(resolveErrorMessage(err) || '顔検査の開始に失敗しました');
    } finally {
      setIsFaceScanTriggering(false);
    }
  };

  const handleDelete = async () => {
    if (!actor || !deleteFn) return;

    setIsDeleting(true);
    try {
      await deleteFn(actor.id);
      setShowDeleteConfirm(false);
      onSuccess?.();
    } catch (err: any) {
      console.error('Failed to delete actor:', err);
      setError(err?.response?.data?.error || err?.message || '削除に失敗しました');
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* ヘッダー */}
        <div className={styles.header}>
          <h2 className={styles.title}>
            {isEditing ? `${titlePrefix}${brandConfig.labels.aiActor}を編集` : `${titlePrefix}${brandConfig.labels.aiActor}を追加`}
          </h2>
          <button type="button" className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        {/* 2カラムレイアウト */}
        <div className={styles.mainContent}>
          {/* 左カラム: フォーム */}
          <div className={styles.formColumn}>
            <form onSubmit={handleSubmit} className={styles.form}>
              {/* キャラクターシート画像 */}
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  キャラクターシート画像
                  {!isEditing && <span className={styles.required}>*</span>}
                </label>
                <div className={styles.fileInputWrapper}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={handleFileChange}
                    className={styles.fileInput}
                  />
                  <button
                    type="button"
                    className="btn-modal btn-modal-secondary"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    ファイルを選択
                  </button>
                  <span className={styles.fileName}>
                    {characterSheet?.name || (isEditing ? '変更する場合のみ選択' : '選択されていません')}
                  </span>
                </div>
                <p className={styles.helpText}>
                  全身（FRONT/LEFT/RIGHT/BACK）と顔（SERIOUS/SMILING）が含まれた画像
                </p>
              </div>

              {/* 内部名 */}
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  内部名 <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="model_takeshi"
                  className={styles.input}
                  disabled={isEditing}
                />
                {isEditing && (
                  <p className={styles.helpText}>内部名は変更できません</p>
                )}
              </div>

              {/* 表示名 */}
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  表示名 <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="モデル4"
                  className={styles.input}
                />
              </div>

              {/* 性別 */}
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  性別 {!isCharacter && <span className={styles.required}>*</span>}
                </label>
                <div className={styles.radioGroup}>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="gender"
                      value="male"
                      checked={gender === 'male'}
                      onChange={() => setGender('male')}
                    />
                    男性
                  </label>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="gender"
                      value="female"
                      checked={gender === 'female'}
                      onChange={() => setGender('female')}
                    />
                    女性
                  </label>
                  {/* キャラクターのみ性別「未指定」を選べる (人間俳優は従来どおり必須) */}
                  {isCharacter && (
                    <label className={styles.radioLabel}>
                      <input
                        type="radio"
                        name="gender"
                        value=""
                        checked={gender === ''}
                        onChange={() => setGender('')}
                      />
                      未指定
                    </label>
                  )}
                </div>
              </div>

              {/* 年齢範囲（キャラクターは対象外） */}
              {!isCharacter && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>年齢範囲</label>
                  <div className={styles.ageRange}>
                    <input
                      type="number"
                      value={minAge}
                      onChange={(e) => setMinAge(e.target.value)}
                      placeholder="20"
                      className={styles.inputSmall}
                      min="0"
                      max="100"
                    />
                    <span>〜</span>
                    <input
                      type="number"
                      value={maxAge}
                      onChange={(e) => setMaxAge(e.target.value)}
                      placeholder="40"
                      className={styles.inputSmall}
                      min="0"
                      max="100"
                    />
                    <span>歳</span>
                  </div>
                </div>
              )}

              {/* 人種 */}
              <div className={styles.formGroup}>
                <label className={styles.label}>人種</label>
                <select
                  value={ethnicity}
                  onChange={(e) => handleEthnicityChange(e.target.value)}
                  className={styles.select}
                >
                  <option value="">指定なし</option>
                  <option value="jp">日本人</option>
                  <option value="foreigner">外国人</option>
                  <option value="character">キャラクター</option>
                </select>
              </div>

              {/* キャラクターの特徴・設定テキスト (キャラクターのみ)。character_traits に
                  保存され、シナリオ生成・動画生成の AI プロンプトへそのまま追加される */}
              {isCharacter && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>キャラクターの特徴・設定</label>
                  <textarea
                    value={characterTraits}
                    onChange={(e) => setCharacterTraits(e.target.value)}
                    placeholder={'例: 身長は15cm。腕が伸びる。'}
                    className={styles.textarea}
                    rows={4}
                    maxLength={500}
                  />
                  <p className={styles.helpText}>
                    入力したテキストは、シナリオ生成や動画生成の際にそのままAIへのプロンプト（指示文）として追加されます（500文字まで）
                  </p>
                </div>
              )}

              {/* face-safe 検査状態 (編集対象がキャラクター俳優のときのみ表示する読み取り専用行)。
                  保存済みの検査結果 (actor 行) を表示するもので、フォームの ethnicity 変更には追随しない */}
              {actor && isCharacterActor(actor) && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>顔検査</label>
                  <p className={styles.helpText} style={{ marginTop: 0 }}>
                    {getFaceScanStatusLabel(actor.character_sheet_face_scan_status)}
                  </p>
                  {/* 未検査/エラー/永久pending のときのみ表示 (safe は不要・restricted は
                      「判定ガチャ」防止のためユーザー起点の再検査を許可しない) */}
                  {onTriggerFaceScan
                    && (actor.character_sheet_face_scan_status == null
                      || actor.character_sheet_face_scan_status === 'error') && (
                    <button
                      type="button"
                      className={styles.faceScanTriggerButton}
                      onClick={handleTriggerFaceScan}
                      disabled={isFaceScanTriggering}
                    >
                      {isFaceScanTriggering ? '開始中…' : '検査を実行'}
                    </button>
                  )}
                </div>
              )}

              {/* タグ */}
              <div className={styles.formGroup}>
                <label className={styles.label}>タグ</label>
                <input
                  type="text"
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                  placeholder="ビジネス, 爽やか"
                  className={styles.input}
                />
                <p className={styles.helpText}>カンマ区切りで複数入力できます</p>
              </div>

              {error && <div className={styles.error}>{error}</div>}
            </form>
          </div>

          {/* 右カラム: プレビュー */}
          <div className={styles.previewColumn}>
            {/* 顔画像 */}
            {existingFaceImageUrl && (
              <div className={styles.facePreviewSection}>
                <div
                  className={styles.facePreviewCircle}
                  onClick={() => setLightboxImage({ src: existingFaceImageUrl, alt: '顔画像' })}
                >
                  <img src={existingFaceImageUrl} alt="顔画像" />
                </div>
              </div>
            )}

            {/* キャラクターシート */}
            {displayPreviewUrl ? (
              <div
                className={styles.previewImageContainer}
                onClick={() => setLightboxImage({ src: displayPreviewUrl, alt: 'キャラクターシート' })}
              >
                <img src={displayPreviewUrl} alt="キャラクターシート" />
              </div>
            ) : (
              <div className={styles.previewPlaceholder}>
                キャラクターシート画像を選択してください
              </div>
            )}

            {/* 保存済みシートから抽出したポーズ (機能フラグoffでposesキーが無いときは何も出さない) */}
            {actor && (
              <ActorPoseList
                key={actor.id}
                poses={actor.poses}
                state={actor.pose_extraction_state}
                requestId={actor.pose_extraction_request_id}
                onOpenImage={(src, alt) => setLightboxImage({ src, alt })}
                onRequestExtraction={
                  onRequestPoseExtraction && existingCharacterSheetUrl
                    ? () => onRequestPoseExtraction(actor.id)
                    : undefined
                }
              />
            )}
          </div>
        </div>

        {/* フッター */}
        <div className={styles.footer}>
          <div className={styles.footerLeft}>
            {isEditing && deleteFn && (
              showDeleteConfirm ? (
                <div className={styles.deleteConfirm}>
                  <span className={styles.deleteConfirmText}>本当に削除しますか？</span>
                  <button
                    type="button"
                    className={styles.btnDanger}
                    onClick={handleDelete}
                    disabled={isDeleting}
                  >
                    {isDeleting ? '削除中...' : '削除する'}
                  </button>
                  <button
                    type="button"
                    className="btn-modal btn-modal-secondary"
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={isDeleting}
                  >
                    キャンセル
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className={styles.btnDangerOutline}
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isLoading}
                >
                  削除
                </button>
              )
            )}
          </div>
          <div className={styles.footerRight}>
            <button
              type="button"
              className="btn-modal btn-modal-secondary"
              onClick={onClose}
              disabled={isLoading || isDeleting}
            >
              キャンセル
            </button>
            <button
              type="button"
              className="btn-modal"
              onClick={handleSubmit}
              disabled={isLoading || isDeleting}
            >
              {isLoading ? '保存中...' : isEditing ? '更新' : '追加'}
            </button>
          </div>
        </div>
      </div>

      {/* 画像拡大モーダル */}
      {lightboxImage && (
        <ImageLightbox
          src={lightboxImage.src}
          alt={lightboxImage.alt}
          onClose={() => setLightboxImage(null)}
        />
      )}
    </div>,
    document.body
  );
}
